import React, { useEffect } from 'react';
import EIT from '../assets/images/EITlogo.png';
import { motion } from 'framer-motion';

const teamMembers = [
  { name: 'Ebenezer Dadzie',       role: 'Managing Director',    img: require('../assets/images/ebenezer.jpeg') },
  { name: 'Rosemond Esi Abakah',   role: 'Director of Training', img: require('../assets/images/rosemond.jpg') },
  { name: 'Isaac Oboh Yamoah',     role: 'Marketing Manager',    img: require('../assets/images/isaac.jpeg') },
  { name: 'Godwin Kingsley Woode', role: 'Software Engineer',    img: require('../assets/images/avatar.png') },
  { name: 'Samson Mayeem',         role: 'Software Engineer',    img: require('../assets/images/samson.jpg') },
  { name: 'Lawrence Ephrim',       role: 'Software Engineer',    img: require('../assets/images/avatar.png') },
];

export default function About() {
  useEffect(() => {
    document.title = 'EIT Ghana Limited - About Us';
  }, []);

  return (
    <motion.div
      className="pt-16 bg-gray-50 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >

      {/* ── BANNER CARD ── */}
      <section className="pt-8 pb-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-20">
          <motion.div
            className="bg-blueblack rounded-3xl p-10 lg:p-16 relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-yellowcolor/10 rounded-full" />
            <div className="absolute -bottom-10 right-32 w-36 h-36 bg-seablue/20 rounded-full" />
            <div className="relative z-10 max-w-2xl">
              <p className="text-yellowcolor font-semibold text-xs uppercase tracking-widest mb-4">About Us</p>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">Who We Are</h1>
              <p className="text-gray-300 text-lg leading-relaxed">
                EIT Ghana Limited is a technology company that provides innovative solutions to businesses
                and individuals — a team of passionate professionals committed to solving real-world
                problems through technology.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── VISION & MISSION BENTO ── */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-20">

          <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-5">

            {/* Logo card */}
            <motion.div
              className="lg:col-span-4 bg-white rounded-3xl p-10 shadow-sm flex items-center justify-center min-h-[200px]"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center gap-4 text-center">
                <img src={EIT} alt="EIT Ghana Limited" className="w-32 lg:w-40" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">EIT Ghana Limited</span>
              </div>
            </motion.div>

            {/* Vision card */}
            <motion.div
              className="lg:col-span-4 bg-white rounded-3xl p-8 shadow-sm flex flex-col gap-4 border-l-4 border-seablue"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-xl">🔭</div>
              <div>
                <p className="text-seablue font-semibold text-xs uppercase tracking-widest mb-2">01 / Vision</p>
                <h3 className="text-xl font-bold text-blueblack mb-3">Our Vision</h3>
                <p className="text-gray-500 leading-relaxed text-sm">
                  To be the leading catalyst for entrepreneurial success in Ghana and beyond.
                </p>
              </div>
            </motion.div>

            {/* Mission card */}
            <motion.div
              className="lg:col-span-4 bg-blueblack rounded-3xl p-8 flex flex-col gap-4 border-l-4 border-yellowcolor"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 bg-yellowcolor/20 rounded-xl flex items-center justify-center text-xl">🎯</div>
              <div>
                <p className="text-yellowcolor font-semibold text-xs uppercase tracking-widest mb-2">02 / Mission</p>
                <h3 className="text-xl font-bold text-white mb-3">Our Mission</h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  To empower aspiring entrepreneurs, startups and MSMEs with the knowledge, resources,
                  and support they need to succeed in today's dynamic business landscape.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── TEAM BENTO ── */}
      <section className="py-8 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-20">

          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <p className="text-seablue font-semibold text-xs uppercase tracking-widest mb-2">People</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-blueblack">Meet Our Team</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-5">
            {teamMembers.map(({ name, role, img }, i) => (
              <motion.div
                key={name}
                className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="relative overflow-hidden h-52">
                  <img
                    src={img}
                    alt={name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blueblack/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-blueblack text-sm">{name}</h3>
                  <p className="text-seablue text-xs mt-0.5 font-semibold">{role}</p>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

    </motion.div>
  );
}
